import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Job } from '@/lib/api/jobs';
import { updateJobStatus } from '@/lib/api/jobs';
import { jobPath } from '@/lib/utils/navPaths';

// Utility: copy text to clipboard and show brief visual feedback
function useCopyFeedback() {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    }).catch(() => {});
  };
  return { copied, copy };
}

interface JobRowProps {
  job: Job;
  onEdit?: (job: Job) => void;
  onEditSchedule?: (job: Job) => void;
  onDuplicate?: (job: Job) => void;
  onDelete?: (job: Job) => void;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

// Status cycle for quick one-click transitions (#19)
const STATUS_CYCLE: Record<string, string> = {
  'scheduled': 'in progress',
  'in progress': 'completed',
  'in_progress': 'completed',
  'completed': 'on hold',
  'on hold': 'scheduled',
};

export const JobRow = ({ job, onEdit, onEditSchedule, onDuplicate, onDelete, isSelected, onToggleSelect }: JobRowProps) => {
  const [showActions, setShowActions] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const { copied, copy } = useCopyFeedback();
  const [transitioning, setTransitioning] = useState(false);

  const handleStatusCycle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const next = STATUS_CYCLE[job.status?.toLowerCase() || ''];
    if (!next || transitioning) return;
    setTransitioning(true);
    try {
      await updateJobStatus(job.id.toString(), next);
      // Reload is triggered by parent's onEdit callback if passed; otherwise we can signal via the edit handler
      if (onEdit) onEdit({ ...job, status: next });
    } catch {
      // silently fail — user can retry
    } finally {
      setTransitioning(false);
    }
  };

  const getStatusClasses = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'in progress' || statusLower === 'in_progress') {
      return 'bg-success/10 text-success ring-1 ring-inset ring-success/20';
    }
    if (statusLower === 'completed') {
      return 'bg-primary/10 text-primary ring-1 ring-inset ring-primary/20';
    }
    if (statusLower === 'scheduled') {
      return 'bg-blue-500/10 text-blue-500 ring-1 ring-inset ring-blue-500/20';
    }
    if (statusLower === 'on hold') {
      return 'bg-yellow-500/10 text-yellow-500 ring-1 ring-inset ring-yellow-500/20';
    }
    return 'bg-gray-500/10 text-gray-500 ring-1 ring-inset ring-gray-500/20';
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return 'Not scheduled';
    return new Date(date).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <tr className={`hover:bg-primary/5 transition-colors ${isSelected ? 'bg-primary/5' : ''}`}>
      {onToggleSelect !== undefined && (
        <td className="pl-4 py-3 w-10">
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={onToggleSelect}
            onClick={e => e.stopPropagation()}
            className="rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
          />
        </td>
      )}
      <td className="px-4 py-3 text-sm font-bold text-primary">
        <div className="flex items-center gap-1 group">
          <Link to={jobPath(job.clientId, job.id)} className="hover:underline">
            {job.jobNumber}
          </Link>
          <button
            type="button"
            title="Copy job number"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); copy(job.jobNumber ?? '', 'jobNum'); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary p-0.5 rounded"
          >
            <span className="material-symbols-outlined text-xs">{copied === 'jobNum' ? 'check' : 'content_copy'}</span>
          </button>
        </div>
      </td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900">
        <Link to={jobPath(job.clientId, job.id)} className="hover:underline">
          {job.title}
        </Link>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {job.client}
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusClasses(job.status)}`}>
          {job.status}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {job.crew || 'Unassigned'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {formatDate(job.startDate)}
      </td>
      <td className="px-4 py-3 text-right overflow-visible">
        <div className="relative inline-block">
          <button
            ref={buttonRef}
            type="button"
            className="text-gray-600 hover:text-primary p-2 rounded-lg hover:bg-primary/10 inline-flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              console.log('Actions button clicked, current state:', showActions);
              
              // Calculate position
              if (buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setDropdownPosition({
                  top: rect.bottom + 4,
                  right: window.innerWidth - rect.right,
                });
              }
              
              setShowActions(!showActions);
            }}
          >
            <span className="material-symbols-outlined text-base">more_vert</span>
          </button>
          
          {showActions && (
            <>
              <div
                className="fixed inset-0 z-[9998]"
                onClick={() => {
                  console.log('Backdrop clicked');
                  setShowActions(false);
                }}
              />
              <div 
                className="fixed bg-white border border-gray-200 shadow-2xl rounded-lg w-48 z-[9999]"
                style={{
                  top: `${dropdownPosition.top}px`,
                  right: `${dropdownPosition.right}px`,
                }}
              >
                <div className="py-1">
                {/* One-click status transition (#19) */}
                {STATUS_CYCLE[job.status?.toLowerCase() || ''] && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-blue-700 hover:bg-blue-50 text-left transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusCycle(e);
                      setShowActions(false);
                    }}
                    disabled={transitioning}
                  >
                    <span className="material-symbols-outlined text-base">swap_horiz</span>
                    {transitioning ? 'Updating…' : `→ ${STATUS_CYCLE[job.status?.toLowerCase() || '']}`}
                  </button>
                )}
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-900 hover:bg-primary/10 text-left transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Edit clicked');
                    if (onEdit) onEdit(job);
                    setShowActions(false);
                  }}
                >
                  <span className="material-symbols-outlined text-base">edit</span>
                  Edit
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-900 hover:bg-primary/10 text-left transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Edit Schedule clicked');
                    if (onEditSchedule) onEditSchedule(job);
                    setShowActions(false);
                  }}
                >
                  <span className="material-symbols-outlined text-base">calendar_month</span>
                  Edit Schedule
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-900 hover:bg-primary/10 text-left transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Duplicate clicked');
                    if (onDuplicate) onDuplicate(job);
                    setShowActions(false);
                  }}
                >
                  <span className="material-symbols-outlined text-base">content_copy</span>
                  Duplicate
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-error hover:bg-error/10 text-left transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Delete clicked');
                    if (onDelete) onDelete(job);
                    setShowActions(false);
                  }}
                >
                  <span className="material-symbols-outlined text-base">delete</span>
                  Delete
                </button>
              </div>
            </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

